// File Metadata:
// Filename: NewReceptionForm.js
// Version: 1.00
// Created: 2022-09-28 22:17:32
// Last Modified: 2025-04-01 17:27:27
// Authors: [Your Name]
// Description: A brief summary of what this file does

import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { useAddNewReceptionMutation } from '../api/receptionsApiSlice'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faSave } from '@fortawesome/free-solid-svg-icons'
import { LOADERS } from '../../config/constants'
import useAuth from '../../hooks/useAuth'

const NewReceptionForm = ({ vendors }) => {
	const { username } = useAuth
	const [addNewReception, { isLoading, isSuccess, isError, error }] = useAddNewReceptionMutation()

	const navigate = useNavigate()

	/* 
		deliveryDate R
		unloadLocation R
		unloadedBy R
		vendorNumber
		vendorName R
		handlingUnits: {
			dec
			euro
			inoutic
			crate
			pal
		},
		receptionDate
		goodsReceipt
		putawayStatus
		notes
		inboundCompleted bool
		inboundStatus [string]
		registrationBy string
		receptionBy string
		putawayBy [string]
	*/

	const [deliveryDate, setDeliveryDate] = useState('')
	const [unloadLocation, setUnloadLocation] = useState('')
	const [unloadedBy, setUnloadedBy] = useState(LOADERS[0].name)
	const [vendorNumber, setVendorNumber] = useState(vendors[0].vendorNumber)
	const [vendorName, setVendorName] = useState(vendors[0].vendorName)
	const [tourNumber, setTourNumber] = useState('')
	const [handlingUnits, setHandlingUnits] = useState({})
	const [receptionDate, setReceptionDate] = useState('')
	const [goodsReceipt, setGoodsReceipt] = useState(false)
	const [putawayStatus, setPutawayStatus] = useState(false)
	const [notes, setNotes] = useState('')

	useEffect(() => {
		if (isSuccess) {
			setDeliveryDate('')
			setUnloadLocation('')
			setUnloadedBy('')
			setVendorNumber('')
			setVendorName('')
			setTourNumber('')
			setHandlingUnits({})
			setReceptionDate('')
			setGoodsReceipt(false)
			setPutawayStatus(false)
			setNotes('')
			navigate('/dash/receptions')
		}
	}, [isSuccess, navigate])

	const onDeliveryDateChanged = e => setDeliveryDate(e.target.value)
	const onUnloadLocationChanged = e => setUnloadLocation(e.target.value)
	const onUnloadedByChanged = e => setUnloadedBy(e.target.value)
	const onVendorNumberChanged = e => setVendorNumber(e.target.value)
	const onTourNumberChanged = e => setTourNumber(e.target.value)
	const onVendorNameChanged = e => {
		setVendorName(e.target.value)
		let foundVendor = vendors.find(vendor => {
			if (vendor.vendorName === e.target.value) return vendor.vendorNumber
		})
		console.log(foundVendor)
		setVendorNumber(foundVendor.vendorNumber)
	}
	const onHandlingUnitsChanged = e => setHandlingUnits(e.target.value)
	const onReceptionDateChanged = e => setReceptionDate(e.target.value)
	const onGoodsReceiptChanged = e => setGoodsReceipt(e.target.value)
	const onPutawayStatusChanged = e => setPutawayStatus(e.target.value)
	const onNotesChanged = e => setNotes(e.target.value)

	const canSave = [deliveryDate, unloadLocation, unloadedBy, vendorName].every(Boolean) && !isLoading

	const onSaveReceptionClicked = async e => {
		e.preventDefault()
		if (canSave) {
			await addNewReception({
				deliveryDate,
				unloadLocation,
				unloadedBy,
				vendorNumber,
				vendorName,
				tourNumber,
				handlingUnits,
				receptionDate,
				goodsReceipt,
				putawayStatus,
				notes,
				inboundCompleted: false,
				inboundStatus: ['1'],
				registrationBy: username,
				receptionBy: '',
				putawayBy: ''
			})
		}
	}

	const unloadedByOptions = LOADERS.map(unloader => {
		return (
			<option key={unloader.id} value={unloader.name}>
				{unloader.name}
			</option>
		)
	})

	const vendorNameOptions = vendors.map(vendor => {
		return (
			<option key={vendor.id} value={vendor.vendorName}>
				{vendor.vendorName}
			</option>
		)
	})

	const errClass = isError ? 'errmsg' : 'offscreen'
	const validDeliveryDateClass = !deliveryDate ? 'form__input--incomplete' : ''
	const validUnloadLocationClass = !unloadLocation ? 'form__input--incomplete' : ''
	const validUnloadedByClass = !unloadedBy ? 'form__input--incomplete' : ''
	const validVendorNameClass = !vendorName ? 'form__input--incomplete' : ''

	const content = (
		<>
			<p className={errClass}>{error?.data?.message}</p>

			<form className='form' onSubmit={onSaveReceptionClicked}>
				<div className='form__title-row'>
					<h2>New Reception</h2>
					<div className='form__action-buttons'>
						<button className='icon-button' title='Save' disabled={!canSave}>
							<FontAwesomeIcon icon={faSave} />
						</button>
					</div>
				</div>
				<fieldset>
					<label className='form__label' htmlFor='reception-deliveryDate'>
						Delivery Date:
						<input
							className={`form__input ${validDeliveryDateClass}`}
							placeholder='yyyy-dd-mm'
							id='reception-deliveryDate'
							name='deliveryDate'
							type='date'
							autoComplete='off'
							value={deliveryDate}
							onChange={onDeliveryDateChanged}
						/>
					</label>

					<label className='form__label' htmlFor='reception-unloadLocation'>
						Unload Location:
						<input
							className={`form__input ${validUnloadLocationClass}`}
							id='reception-unloadLocation'
							name='unloadLocation'
							type='text'
							value={unloadLocation}
							onChange={onUnloadLocationChanged}
						/>
					</label>

					<label className='form__label' htmlFor='reception-unloadedBy'>
						Unloaded By:
						<select
							className={`form__input ${validUnloadedByClass}`}
							id='reception-unloadedBy'
							name='unloadedBy'
							value={unloadedBy}
							onChange={onUnloadedByChanged}
						>
							{unloadedByOptions}
						</select>
					</label>

					<label className='form__label' htmlFor='reception-vendorName'>
						Vendor Name:
						<select
							className={`form__input ${validVendorNameClass}`}
							id='reception-vendorName'
							name='vendorName'
							value={vendorName}
							onChange={onVendorNameChanged}
						>
							{vendorNameOptions}
						</select>
					</label>

					<label className='form__label' htmlFor='reception-vendorNumber'>
						Vendor Number:
						<input
							className={'form__input'}
							id='reception-vendorNumber'
							name='vendorNumber'
							type='text'
							disabled
							autoComplete='off'
							value={vendorNumber}
						/>
					</label>
					<label className='form__label' htmlFor='reception-tourNumber'>
						Tour Number:
						<input
							className={'form__input'}
							id='reception-tourNumber'
							name='tourNumber'
							type='text'
							autoComplete='off'
							value={tourNumber}
							onChange={onTourNumberChanged}
						/>
					</label>
					<label className='form__label' htmlFor='reception-notes'>
						Reception Notes:
						<textarea
							className={'form__input form__input--text'}
							id='note-notes'
							name='notes'
							value={notes}
							onChange={onNotesChanged}
						/>
					</label>
				</fieldset>
			</form>
		</>
	)

	return content
}

export default NewReceptionForm
